// This file is auto-generated by @hey-api/openapi-ts

import type { Client, Options as Options2, TDataShape } from './client';
import { client } from './client.gen';
import type { MvpApiGetAllUserMetricsData, MvpApiGetAllUserMetricsResponses, MvpApiGetMyGoldenMetricsData, MvpApiGetMyGoldenMetricsResponses, MvpApiGetMyMetricsData, MvpApiGetMyMetricsResponses, MvpApiGetUserGoldenMetricsData, MvpApiGetUserGoldenMetricsResponses, MvpApiGetUserMetricsData, MvpApiGetUserMetricsResponses, MvpApiListViewableUsersData, MvpApiListViewableUsersResponses, MvpApiSyncMetricsData, MvpApiSyncMetricsResponses, SsoApiGetMeData, SsoApiGetMeResponses, SsoApiMintCustomTokenData, SsoApiMintCustomTokenResponses } from './types.gen';

export type Options<TData extends TDataShape = TDataShape, ThrowOnError extends boolean = boolean> = Options2<TData, ThrowOnError> & {
    /**
     * You can provide a client instance returned by `createClient()` instead of
     * individual options. This might be also useful if you want to implement a
     * custom client.
     */
    client?: Client;
    /**
     * You can pass arbitrary values through the `meta` object. This can be
     * used to access values that aren't defined as part of the SDK function.
     */
    meta?: Record<string, unknown>;
};

/**
 * Sync Metrics
 *
 * Trigger an incremental lebron metrics sync. Internal API key only.
 */
export const mvpApiSyncMetrics = <ThrowOnError extends boolean = false>(options?: Options<MvpApiSyncMetricsData, ThrowOnError>) => (options?.client ?? client).post<MvpApiSyncMetricsResponses, unknown, ThrowOnError>({
    security: [{ name: 'X-API-Key', type: 'apiKey' }],
    url: '/v0/admin/sync-metrics',
    ...options
});

/**
 * List Viewable Users
 *
 * List the users whose profiles the caller is allowed to view.
 *
 * - augmodo role (or staff/API key): all users
 * - team_lead role: users with the same vendor
 * - labeler (default): only the caller themselves
 *
 * See `viewable_users_qs`, also used by `get_all_user_metrics` so both
 * endpoints share one authorization source of truth.
 */
export const mvpApiListViewableUsers = <ThrowOnError extends boolean = false>(options?: Options<MvpApiListViewableUsersData, ThrowOnError>) => (options?.client ?? client).get<MvpApiListViewableUsersResponses, unknown, ThrowOnError>({
    security: [{ name: 'X-API-Key', type: 'apiKey' }, { scheme: 'bearer', type: 'http' }],
    url: '/v0/users/',
    ...options
});

/**
 * Get All User Metrics
 *
 * Every viewable labeler's headline metrics for one task_type/period (or
 * date range), in a single response -- the batch counterpart to
 * /users/{user_id}/metrics.
 *
 * Visibility follows `viewable_users_qs` (same scoping as GET /users/):
 * augmodo/staff/an internal API key see every vendor, team_lead sees their
 * own vendor, a plain labeler sees only themselves. `task_type` is required,
 * same as the per-user endpoint.
 *
 * Without a date range (the common case): 2 queries total, regardless of
 * how many users are viewable. With a date range: 1 query plus one
 * `team_averages` call (2 queries each) per *distinct vendor* among the
 * viewable users, not one per user -- team_averages depends only on
 * (task_type, vendor_id, period), so it would otherwise be the dominant cost.
 */
export const mvpApiGetAllUserMetrics = <ThrowOnError extends boolean = false>(options?: Options<MvpApiGetAllUserMetricsData, ThrowOnError>) => (options?.client ?? client).get<MvpApiGetAllUserMetricsResponses, unknown, ThrowOnError>({
    security: [{ name: 'X-API-Key', type: 'apiKey' }, { scheme: 'bearer', type: 'http' }],
    url: '/v0/users/metrics',
    ...options
});

/**
 * Get My Metrics
 *
 * The calling user's own metrics, resolved from their authenticated email.
 * `task_type` is required (e.g. OUT_OF_STOCK_CONFIRMATION or COVERAGE_REVIEW).
 * The response includes `role` so the frontend knows whether to show the labeler-picker.
 */
export const mvpApiGetMyMetrics = <ThrowOnError extends boolean = false>(options?: Options<MvpApiGetMyMetricsData, ThrowOnError>) => (options?.client ?? client).get<MvpApiGetMyMetricsResponses, unknown, ThrowOnError>({
    security: [{ name: 'X-API-Key', type: 'apiKey' }, { scheme: 'bearer', type: 'http' }],
    url: '/v0/users/me/metrics',
    ...options
});

/**
 * Get User Metrics
 *
 * A specific user's metrics. Accessible to staff/superusers, internal API keys,
 * the same user, augmodo-role users, or team leads viewing the same vendor.
 * `task_type` is required (e.g. OUT_OF_STOCK_CONFIRMATION or COVERAGE_REVIEW).
 */
export const mvpApiGetUserMetrics = <ThrowOnError extends boolean = false>(options: Options<MvpApiGetUserMetricsData, ThrowOnError>) => (options.client ?? client).get<MvpApiGetUserMetricsResponses, unknown, ThrowOnError>({
    security: [{ name: 'X-API-Key', type: 'apiKey' }, { scheme: 'bearer', type: 'http' }],
    url: '/v0/users/{user_id}/metrics',
    ...options
});

/**
 * Get My Golden Metrics
 *
 * The calling user's quiz + injected golden-dataset accuracy.
 *
 * Deliberately separate from /users/me/metrics: overall_score there reflects only
 * OUT_OF_STOCK_CONFIRMATION/COVERAGE_REVIEW work and never includes this data.
 */
export const mvpApiGetMyGoldenMetrics = <ThrowOnError extends boolean = false>(options?: Options<MvpApiGetMyGoldenMetricsData, ThrowOnError>) => (options?.client ?? client).get<MvpApiGetMyGoldenMetricsResponses, unknown, ThrowOnError>({
    security: [{ name: 'X-API-Key', type: 'apiKey' }, { scheme: 'bearer', type: 'http' }],
    url: '/v0/users/me/golden-metrics',
    ...options
});

/**
 * Get User Golden Metrics
 *
 * A specific user's quiz + injected golden-dataset accuracy. Same access rules
 * as /users/{user_id}/metrics.
 */
export const mvpApiGetUserGoldenMetrics = <ThrowOnError extends boolean = false>(options: Options<MvpApiGetUserGoldenMetricsData, ThrowOnError>) => (options.client ?? client).get<MvpApiGetUserGoldenMetricsResponses, unknown, ThrowOnError>({
    security: [{ name: 'X-API-Key', type: 'apiKey' }, { scheme: 'bearer', type: 'http' }],
    url: '/v0/users/{user_id}/golden-metrics',
    ...options
});

/**
 * Mint Custom Token
 *
 * Exchange a Firebase ID token for a custom token the requesting app's
 * origin can sign in with. The ID token itself is the proof of identity, so
 * this endpoint is unauthenticated. Access requires an active Django user
 * with at least one app grant.
 */
export const ssoApiMintCustomToken = <ThrowOnError extends boolean = false>(options: Options<SsoApiMintCustomTokenData, ThrowOnError>) => (options.client ?? client).post<SsoApiMintCustomTokenResponses, unknown, ThrowOnError>({
    url: '/v0/auth/token',
    ...options,
    headers: {
        'Content-Type': 'application/json',
        ...options.headers
    }
});

/**
 * Get Me
 *
 * The calling user's identity and app access, from the local DB.
 */
export const ssoApiGetMe = <ThrowOnError extends boolean = false>(options?: Options<SsoApiGetMeData, ThrowOnError>) => (options?.client ?? client).get<SsoApiGetMeResponses, unknown, ThrowOnError>({
    security: [{ name: 'X-API-Key', type: 'apiKey' }, { scheme: 'bearer', type: 'http' }],
    url: '/v0/auth/me',
    ...options
});
